/**
 * ChannelIcons.js
 * ===============
 *
 * Brand marks for the notification delivery channels. Slack, Microsoft
 * Teams and PagerDuty are inline SVGs (MUI has no brand icons); the
 * rest map to Material icons so every channel key used by the catalog
 * has exactly one visual.
 *
 * Usage: <ChannelIcon type="slack" size={20} />
 */

import React from 'react';
import SvgIcon from '@mui/material/SvgIcon';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import WebhookIcon from '@mui/icons-material/Webhook';
import SettingsEthernetIcon from '@mui/icons-material/SettingsEthernet';

// ---------------------------------------------------------------------------
// Brand SVGs
// ---------------------------------------------------------------------------

export const SlackIcon = (props) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <path fill="#E01E5A" d="M5.04 15.16a2.52 2.52 0 1 1-2.52-2.52h2.52v2.52zm1.27 0a2.52 2.52 0 0 1 5.04 0v6.31a2.52 2.52 0 1 1-5.04 0v-6.31z" />
    <path fill="#36C5F0" d="M8.83 5.04a2.52 2.52 0 1 1 2.52-2.52v2.52H8.83zm0 1.27a2.52 2.52 0 0 1 0 5.04H2.52a2.52 2.52 0 1 1 0-5.04h6.31z" />
    <path fill="#2EB67D" d="M18.96 8.83a2.52 2.52 0 1 1 2.52 2.52h-2.52V8.83zm-1.27 0a2.52 2.52 0 0 1-5.04 0V2.52a2.52 2.52 0 1 1 5.04 0v6.31z" />
    <path fill="#ECB22E" d="M15.17 18.96a2.52 2.52 0 1 1-2.52 2.52v-2.52h2.52zm0-1.27a2.52 2.52 0 0 1 0-5.04h6.31a2.52 2.52 0 1 1 0 5.04h-6.31z" />
  </SvgIcon>
);

export const TeamsIcon = (props) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    {/* Simplified Microsoft Teams mark */}
    <circle cx="17.05" cy="7.1" r="2.15" fill="#5059C9" />
    <path fill="#5059C9" d="M19.9 10.1h-4.36c-.3 0-.54.24-.54.54v4.96a3.1 3.1 0 0 0 2.4 3.06 3.16 3.16 0 0 0 3.85-3.08v-4.13c0-.74-.6-1.35-1.35-1.35z" />
    <circle cx="11.2" cy="6.2" r="3.05" fill="#7B83EB" />
    <path fill="#7B83EB" d="M14.75 10.1H6.9c-.73 0-1.32.6-1.32 1.32v5.13a5.42 5.42 0 0 0 4.15 5.36 5.5 5.5 0 0 0 6.7-5.36v-5.13c0-.73-.59-1.32-1.32-1.32z" />
    <rect x="1.5" y="7.9" width="11" height="11" rx="1.2" fill="#4B53BC" />
    <path fill="#fff" d="M9.6 11.06H7.83v5.28H6.4v-5.28H4.64V9.85H9.6v1.21z" />
  </SvgIcon>
);

export const PagerDutyIcon = (props) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    {/* PagerDuty "P" mark */}
    <path
      fill="#06AC38"
      d="M7 2.5h6.1c3.9 0 6.4 2.3 6.4 6.1 0 4-2.7 6.3-6.7 6.3H10v3.3H7V2.5zm3 9.7h2.6c2.3 0 3.8-1.2 3.8-3.5 0-2.2-1.4-3.4-3.7-3.4H10v6.9zM7 19.4h3v2.9H7v-2.9z"
    />
  </SvgIcon>
);

// ---------------------------------------------------------------------------
// Unified lookup
// ---------------------------------------------------------------------------

const ICONS = {
  in_app:    NotificationsOutlinedIcon,
  email:     EmailOutlinedIcon,
  websocket: SettingsEthernetIcon,
  slack:     SlackIcon,
  teams:     TeamsIcon,
  pagerduty: PagerDutyIcon,
  webhook:   WebhookIcon,
};

export const CHANNEL_META = {
  in_app:    { label: 'In-app',          blurb: 'Bell drawer inside Predict.ai' },
  email:     { label: 'Email',           blurb: 'Sent to your account email' },
  websocket: { label: 'WebSocket',       blurb: 'Realtime push on your user channel — for bots and live dashboards' },
  slack:     { label: 'Slack',           blurb: 'Post to a Slack channel via an incoming webhook' },
  teams:     { label: 'Microsoft Teams', blurb: 'Post a card to a Teams channel via a workflow webhook' },
  pagerduty: { label: 'PagerDuty',       blurb: 'Trigger incidents through the Events API' },
  webhook:   { label: 'Webhook',         blurb: 'JSON POST to any HTTPS endpoint, HMAC-signed' },
};

const ChannelIcon = ({ type, size = 20, sx, ...rest }) => {
  const Cmp = ICONS[type] || WebhookIcon;
  return <Cmp sx={{ fontSize: size, ...sx }} {...rest} />;
};

export default ChannelIcon;
